import { useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";
import { masterDataService } from "../api/enhancedApi";

// 币种列表基本不变，缓存时间比机场等参考数据更长一些
const CURRENCY_STALE_TIME = 30 * 60 * 1000;
const CURRENCY_CACHE_TIME = 60 * 60 * 1000;
const CURRENCY_QUERY_KEY = ["reference-data", "currencies"];

function getErrorMessage(error, fallback) {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail) && detail.length > 0) {
        return detail[0]?.msg || fallback;
    }
    if (typeof detail === "string") {
        return detail;
    }
    return error?.message || fallback;
}

/**
 * 系统支持的币种 —— 单一来源。
 *
 * 数据来自后端 `GET /v1/exchange-rates/currencies`（即后端常量 `COMMON_CURRENCIES`）。
 * 汇率维护、成本录入、合同录入等所有币种下拉都应从这里取，避免各处硬编码互相对不上：
 * 以前成本录入写死 4 种、合同写死另 4 种、汇率页走后端 11 种，三处不一致就是这么来的。
 *
 * 用法：
 *   const { currencyOptions } = useCurrencies();        // [{value:"USD", label:"USD - 美元"}]
 *   const { simpleCurrencyOptions } = useCurrencies();  // [{value:"USD", label:"USD"}]  紧凑显示
 */
export const useCurrencies = (options = {}) => {
    const queryClient = useQueryClient();
    const query = useQuery(
        CURRENCY_QUERY_KEY,
        async () => {
            const res = await masterDataService.exchangeRate.getCurrencies();
            return res?.data?.currencies || [];
        },
        {
            staleTime: CURRENCY_STALE_TIME,
            cacheTime: CURRENCY_CACHE_TIME,
            refetchOnWindowFocus: false,
            ...options,
        },
    );

    const currencies = query.data || [];

    // "USD - 美元"：带中文名，给大多数币种下拉用
    const currencyOptions = useMemo(
        () => currencies.map((c) => ({ value: c.code, label: `${c.code} - ${c.name}` })),
        [currencies],
    );

    // "USD"：纯代码，给需要紧凑显示的下拉用
    const simpleCurrencyOptions = useMemo(
        () => currencies.map((c) => ({ value: c.code, label: c.code })),
        [currencies],
    );

    return {
        currencies,
        currencyOptions,
        simpleCurrencyOptions,
        loading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error ? getErrorMessage(query.error, "获取币种列表失败") : null,
        refreshCurrencies: () => queryClient.invalidateQueries(CURRENCY_QUERY_KEY),
    };
};

export default useCurrencies;
